import { useCallback } from 'react';
import { Typography } from '@sweep-io/sweep-design';
import { ExpandButtonSimple } from '../../../../common/ExpandButtonSimple';
import { TagsLine } from './TagsLine';
import Collapse from '@mui/material/Collapse';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useSelector } from 'react-redux';
import { parseIdToValue, parseIdToValueAndNameToLabel } from './utils';
import { selectAccountUsers } from '../../../../../reducers/accountUsersReducer';
import { UserInputTypes } from '@server/user-inputs';
import { useConfigurationTags } from './useConfigurationTags';
import { SectionBox } from '../../helpers';
import { selectParserInfo } from '../../../../../reducers/global/parserReducer';
import { InlineAutocompleteItem } from '../../../../common/InlineAutocomplete/InlineAutocomplete';
import { UserStatus } from '../../../../../types/UserInfoTypes';
import { useTags } from './useTags';
import { selectTagsList } from '../../../../../reducers/documentation/userInputsReducer';
import { selectUserInputsByConfigurationId } from '../../../../../reducers/documentation/userInputsReducer';
import { useSearchContext } from '../../search/SearchContext';
import { HighlightedSection } from '../../search/highlight/HighlightedSection';
import { useSectionExpansion } from '../../search/useSectionExpansion';
import { BASE_SECTION_EXPANSION } from '../../search/consts';

interface TagsSectionProps {
  configurationItemId: string;
  configurationItemType: string;
  crmOrgId: string;
  objectNames: string[];
}

export const TagsSection = ({
  configurationItemId,
  configurationItemType,
  crmOrgId,
  objectNames,
}: TagsSectionProps) => {
  const { searchTerm, sectionExpansion } = useSearchContext();
  const { isExpanded, setIsExpanded } = useSectionExpansion({
    searchTerm,
    sectionExpanded: sectionExpansion.tags,
    defaultExpanded: BASE_SECTION_EXPANSION.tags,
  });

  const tags = useSelector(selectTagsList);
  const users = useSelector(selectAccountUsers) ?? [];
  const onlyActiveUsers = users.filter((user) => user.status === UserStatus.Active);
  const configurationTags = useSelector(selectUserInputsByConfigurationId(configurationItemId));
  const _parsedUsers = parseIdToValueAndNameToLabel(onlyActiveUsers);
  const parserInfo = useSelector(selectParserInfo(crmOrgId));

  const { onTagClick } = useTags(crmOrgId);
  const { onSelectItem, onCreateTag, onDeleteItem } = useConfigurationTags(
    configurationItemId,
    crmOrgId,
  );

  const _onSelectItem = useCallback(
    (item: InlineAutocompleteItem, parentKey: UserInputTypes) => {
      const notParsedObjects = objectNames?.filter(
        (name) => !parserInfo?.parsedObjectNames?.includes(name),
      );
      onSelectItem(item, parentKey, notParsedObjects, configurationItemType);
    },
    [onSelectItem, objectNames, parserInfo?.parsedObjectNames, configurationItemType],
  );

  const _onCreateTag = useCallback(
    (value: string) => {
      const notParsedObjects = objectNames?.filter(
        (name) => !parserInfo?.parsedObjectNames?.includes(name),
      );
      onCreateTag(value, notParsedObjects, configurationItemType);
    },
    [onCreateTag, objectNames, parserInfo?.parsedObjectNames, configurationItemType],
  );

  return (
    <SectionBox data-section="tags">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        onClick={() => {
          setIsExpanded(!isExpanded);
        }}
        sx={{ cursor: 'pointer' }}
        data-testid="tags-section-header"
      >
        <Typography variant="h5-medium">Custom tags</Typography>
        <ExpandButtonSimple buttonSize="tiny" isExpanded={isExpanded} />
      </Box>

      <Collapse in={isExpanded}>
        <HighlightedSection section="tags">
          <Stack pl={2} pt={1.5}>
            <TagsLine
              title="Tags"
              placeholder="Select or create value"
              onSelectItem={(item) => _onSelectItem(item, UserInputTypes.Tags)}
              selectedItems={configurationTags?.[UserInputTypes.Tags] ?? []}
              tags={parseIdToValue(tags)}
              onCreate={_onCreateTag}
              onClick={(item) => onTagClick(null as any, item, false)}
              onDeleteItem={(item) =>
                onDeleteItem(item, UserInputTypes.Tags, configurationItemType)
              }
              headerTxt="Search tag or create one"
            />

            <TagsLine
              title="Owner"
              placeholder="Select Owner"
              onSelectItem={(item) => _onSelectItem(item, UserInputTypes.Owner)}
              selectedItems={
                configurationTags?.[UserInputTypes.Owner]
                  ? [configurationTags[UserInputTypes.Owner]]
                  : []
              }
              tags={_parsedUsers}
              onDeleteItem={(item) =>
                onDeleteItem(item, UserInputTypes.Owner, configurationItemType)
              }
              inlineComponentVariant="users"
              headerTxt="Users"
              closeOnItemSelection
              onClick={(item) => onTagClick(null as any, item, false)}
            />

            <TagsLine
              title="Collaborators"
              placeholder="Select Collaborators"
              onSelectItem={(item) => _onSelectItem(item, UserInputTypes.Collaborators)}
              selectedItems={configurationTags?.[UserInputTypes.Collaborators] ?? []}
              tags={_parsedUsers}
              onDeleteItem={(item) =>
                onDeleteItem(item, UserInputTypes.Collaborators, configurationItemType)
              }
              inlineComponentVariant="users"
              noBottomBorder
              headerTxt="Users"
              onClick={(item) => onTagClick(null as any, item, false)}
            />
          </Stack>
        </HighlightedSection>
      </Collapse>
    </SectionBox>
  );
};
